import React from 'react'
import { Teacher } from '../providers/context'
import { TextInput, CharcoalInput } from '../components/form/textInput'
import { IconCaretDown, IconCaretRight, IconCaretUp, IconSearch } from '../icons/library'
import { ThreePanelLayout } from '../layouts/threePanel'
import { LocalTable } from '../components/table'
import { useRequest } from '../request'
import { ColumnDef } from '@tanstack/react-table'
import { Badge, BadgeStyle } from '../components/simple/badge'
import { Tooltip } from '../components/simple/tooltip'

type RowData = {
  name: string
  type: 'student' | 'guardian' | 'family'
  class: string
  uniqueWithYou: {
    sent: number
    received: number
  }
  anyWithYou: {
    sent: number
    received: number
  }
  allWithAnyTeacher: {
    sent: number
    received: number
  }
  logins: number
  individuals?: RowData[] // only for type === 'family'
}

const columns: ColumnDef<RowData>[] = [
  {
    header: 'Name',
    accessorKey: 'name',
    cell: ({ row }) =>
      row.original.type === 'family' ? (
        <b>
          {row.original.name} {row.getIsExpanded() ? <IconCaretDown opacity={0.5} /> : <IconCaretRight opacity={0.5} />}
        </b>
      ) : row.original.type === 'student' ? (
        <Badge badgeStyle={BadgeStyle.student} className="text-lg">
          {row.original.name}
        </Badge>
      ) : (
        <Badge badgeStyle={BadgeStyle.guardian} className="text-lg">
          {row.original.name}
        </Badge>
      ),
  },
  {
    header: 'Class',
    accessorKey: 'class',
  },
  {
    header: 'Unique with you',
    accessorKey: 'uniqueWithYou',
    cell: ({ row }) => (
      <div>
        <Tooltip text="One to one message to you">
          <span className="text-red-600">↑</span>
        </Tooltip>
        {row.original.uniqueWithYou.sent}{' '}
        <Tooltip text="One to one message from you">
          <span className="text-green-600">↓</span>
        </Tooltip>
        {row.original.uniqueWithYou.received}
      </div>
    ),
  },
  {
    header: 'Any with you',
    accessorKey: 'anyWithYou',
    cell: ({ row }) => (
      <div>
        <Tooltip text="All messages to you">
          <span className="text-red-600">↑</span>
        </Tooltip>
        {row.original.anyWithYou.sent}{' '}
        <Tooltip text="All messages (including group) from you">
          <span className="text-green-600">↓</span>
        </Tooltip>
        {row.original.anyWithYou.received}
      </div>
    ),
  },
  {
    header: 'All with any teacher',
    accessorKey: 'allWithAnyTeacher',
    cell: ({ row }) => (
      <div>
        <Tooltip text="Messages to all teachers">
          <span className="text-red-600">↑</span>
        </Tooltip>
        {row.original.allWithAnyTeacher.sent}{' '}
        <Tooltip text="Messages from all teachers">
          <span className="text-green-600">↓</span>
        </Tooltip>
        {row.original.allWithAnyTeacher.received}
      </div>
    ),
  },
  {
    header: 'Logins',
    accessorKey: 'logins',
  },
]

export const YourStatsPage: React.FC<{ user: Teacher }> = ({ user }) => {
  const [search, setSearch] = React.useState('')

  const { data } = useRequest<RowData[]>('/frontend_api/getYourStats')

  return (
    <ThreePanelLayout
      noMobile
      user={user}
      left={
        <>
          <TextInput
            className={`rounded-full outline outline-1 outline-charcoal-text ${CharcoalInput}`}
            value={search}
            onChange={setSearch}
            placeholder="Search"
            icon={<IconSearch />}
          />
        </>
      }
    >
      <div className="flex-grow overflow-auto bg-lightBlue p-10">
        <div className="flex flex-col gap-2 rounded bg-white p-3 shadow-lg">
          <div className="text-2xl font-semibold">Communication stats for your students</div>
          {data && <LocalTable data={data} search={search} columns={columns} getSubRows={(r) => r.individuals} />}
        </div>
      </div>
    </ThreePanelLayout>
  )
}
